<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { ElDropdown, ElDropdownItem, ElDropdownMenu, ElInput, ElSkeleton, ElSkeletonItem } from 'element-plus';
import AdminReportItemKpiMijlpaal from '@/views/admin/AdminReportItemKpiMijlpaal.vue';
import StatusSelector from '@/views/general/StatusSelector.vue';
import { CabKPI, CabOrganization, CabReportItem, SELECTOR_CONTAINS, StatusEnum, uuidOf } from 'common';
import { listObjects } from '@/base/callBackend.js';
import { localStorageGetJson, localStorageSetJson } from '@/base/utils.js';
import { Debouncer } from '@/base/debouncer.js';
import MenuIcon from '@/views/icons/MenuIcon.vue';

const STORAGE_SEARCH_TEXT_ID = `TabVecozo:searchText`;
const STORAGE_STATUS_SET_ID  = `TabVecozo:statusSet`;
const MAX_RIS_TO_SHOW        = 50;

const allOrganizations    = ref<CabOrganization[]>([]);
const searchText          = ref<string>(localStorageGetJson(STORAGE_SEARCH_TEXT_ID) ?? '');
const searchTextDebounced = ref<string>(searchText.value);
const statusSet           = ref<StatusEnum[]>(localStorageGetJson(STORAGE_STATUS_SET_ID) ?? []);
const reloadTrigger       = ref<number>(0);
const loadingSub          = ref(true);
const allReportItems      = ref<CabReportItem[]>([]);
const allVecozos          = computed(() => Array.from(new Set(allReportItems.value.map(item => item.vecozoId))).filter(v => v).sort());
const limitedReportItems  = ref<CabReportItem[]>([]);
const filteredReportItems = ref<CabReportItem[]>([]);
const filteredKpis        = ref<CabKPI[]>([]);
const numFound            = ref<number>(-1);
const numNotShown         = ref<number>(-1);
const occupation          = computed<Map<StatusEnum, number>>(() => {
    if (loadingSub.value) {
        return new Map<StatusEnum, number>();
    } else {
        return limitedReportItems.value.reduce((statusCounts, item) => {
            statusCounts.set(item.status!, (statusCounts.get(item.status!) || 0) + 1);
            return statusCounts;
        }, new Map<StatusEnum, number>());
    }
});

const debouncer = new Debouncer(200, async (s: string) => {
    searchTextDebounced.value = s;
    reloadTrigger.value++;
});
onMounted(async () => {
    allOrganizations.value = await listObjects(CabOrganization);
    reloadTrigger.value++;
});
watch(searchText, async (t) => {
    localStorageSetJson(STORAGE_SEARCH_TEXT_ID, t);
    debouncer.trigger(t);
});
watch(statusSet, () => {
    localStorageSetJson(STORAGE_STATUS_SET_ID, statusSet.value);
});
watch(reloadTrigger, async (t) => {
    loadingSub.value  = true;
    numFound.value    = -1;
    numNotShown.value = -1;
    const ris         = await listObjects(CabReportItem, {vecozoId: SELECTOR_CONTAINS + searchTextDebounced.value});
    // check if this is the latest search, ignore if it is not because a later request is running:
    if (reloadTrigger.value === t) {
        allReportItems.value     = ris;
        limitedReportItems.value = ris.slice(0, MAX_RIS_TO_SHOW);
        numFound.value           = ris.length;
        numNotShown.value        = ris.length - limitedReportItems.value.length;
    }
});
watch([limitedReportItems, statusSet], () => {
    filteredReportItems.value = limitedReportItems.value.filter(ri => statusSet.value.includes(ri.status!));
    filteredKpis.value        = Array.from(
        filteredReportItems.value
            .reduce((unique, ri) => {
                // Custom compare logic: Check if `ri.kpi[0]` already exists in the Map by `kpi.uuid`
                if (!unique.has(ri.kpi[0].uuid)) {
                    unique.set(ri.kpi[0].uuid, ri.kpi[0]);
                }
                return unique;
            }, new Map<string, CabKPI>()) // Using Map for custom compare by `kpi.uuid`
            .values(),
    ).sort((a, b) => a.name.localeCompare(b.name));
    loadingSub.value          = false;
});

function allReportItemsFor(kpi: CabKPI): CabReportItem[] {
    return filteredReportItems.value?.filter(ri => ri.kpi[0].uuid === kpi.uuid) ?? [];
}

function selectId(v: string) {
    searchText.value          = v;
    searchTextDebounced.value = v;
}

</script>

<template>
    <div class="flex flex-col mx-28 my-5 h-screen">
        <div class="flex flex-row items-center sticky z-10 top-0 pt-1 pb-2">

            <div class="flex flex-row items-center my-3">
                <div class="text-base pr-2">
                    vecozo id
                </div>
            </div>

            <el-input
                v-model="searchText"
                placeholder="Enter Vecozo ID"
                class="w-56"
            />
            <div v-if="numFound==-1" class="px-4">
                zoeken....
            </div>
            <template v-else>

                <div class="flex flex-row items-center pl-4">
                    gevonden: {{ numFound == 0 ? 'geen items' : numFound == 1 ? '1 item' : `${numFound} items` }}
                </div>
                <div class="flex flex-row items-center pl-1"
                     v-if="1 < allVecozos.length"
                >
                    met {{ allVecozos.length }} vecozo ids
                    <el-dropdown
                        class="font-[500] text-base pr-1"
                        @command="selectId"
                    >
                        <MenuIcon/>
                        <template #dropdown>
                            <el-dropdown-menu>
                                <el-dropdown-item
                                    v-for="v in allVecozos"
                                    :key="v"
                                    :command="v"
                                >
                                    {{ v }}
                                </el-dropdown-item>
                            </el-dropdown-menu>
                        </template>
                    </el-dropdown>
                </div>
                <div v-if="0 < numNotShown">
                    ({{ numNotShown }} items niet getoond)
                </div>
            </template>

            <div class="flex-1">
                <!-- filler-->
            </div>

            <StatusSelector :multi="true"
                            v-model:statusSet="statusSet"
                            :occupation="occupation"
            />
        </div>

        <div class="flex-1 overflow-auto">
            <template v-if="loadingSub">
                <el-skeleton animated :count="3">
                    <template #template>
                        <el-skeleton-item class="h-44"/>
                    </template>
                </el-skeleton>
            </template>

            <template v-else>
                <AdminReportItemKpiMijlpaal
                    v-for="kpi in filteredKpis"
                    :key="uuidOf(kpi)"
                    :kpi="kpi"
                    :reportitems="allReportItemsFor(kpi)"
                    :show-org-theme="true"
                    :all-organizations="allOrganizations"
                />
            </template>
        </div>
    </div>

</template>
